<template>
  <Card class="layout-card" :bordered="false">
    <Tabs
      v-model="activeTab"
      :animated="false"
      class="rate-tabs"
      @on-click="handleTabChange"
    >
      <TabPane label="Apple 汇率" name="apple">
        <div class="tab-panel">
          <div class="toolbar">
            <div class="filter-row">
              <Input
                v-model.trim="searchForm.keyword"
                :maxlength="100"
                clearable
                placeholder="国家、地区或货币代码"
                style="width: 210px"
                @on-enter="handleSearch"
              />
              <Select
                v-model="searchForm.status"
                clearable
                placeholder="状态"
                style="width: 110px"
              >
                <Option :value="0">启用</Option>
                <Option :value="1">禁用</Option>
              </Select>
              <DatePicker
                :value="searchDateRange"
                :editable="false"
                clearable
                format="yyyy-MM-dd"
                type="daterange"
                placeholder="汇率日期"
                style="width: 230px"
                @on-change="handleSearchDateChange"
              />
              <Button
                type="primary"
                icon="ios-search"
                :loading="tableLoading"
                @click="handleSearch"
              >搜索</Button>
              <Button icon="ios-refresh" @click="handleReset">重置</Button>
            </div>
            <Button
              type="primary"
              icon="ios-cloud-upload-outline"
              @click="openAppleImportModal"
            >导入 Apple 汇率</Button>
          </div>

          <div ref="appleTableWrap" class="table-wrap">
            <Table
              border
              stripe
              :height="tableHeight"
              :loading="tableLoading"
              :data="tableData"
              :columns="columns"
            >
              <template #status="{ row }">
                <i-switch
                  :value="row.status === 0"
                  :loading="statusLoadingId === row.id"
                  @on-change="value => handleChangeStatus(row, value)"
                />
              </template>
              <template #action="{ row }">
                <Button
                  type="text"
                  size="small"
                  icon="ios-create-outline"
                  @click="handleOpenEditModal(row)"
                >编辑</Button>
              </template>
              <Pages slot="footer" :page="page" :func="getData" />
            </Table>
          </div>
        </div>
      </TabPane>

      <TabPane label="Apple 财务账单" name="appleFinancial">
        <div class="tab-panel">
          <div class="toolbar apple-financial-toolbar">
            <div class="filter-row">
              <Input
                v-model.trim="appleFinancialSearch.keyword"
                clearable
                placeholder="SKU、商品名称或匹配键"
                style="width: 230px"
                @on-enter="getAppleFinancialData()"
              />
              <Select
                v-model="appleFinancialSearch.processStatus"
                clearable
                placeholder="匹配状态"
                style="width: 150px"
              >
                <Option value="MATCHED">已匹配</Option>
                <Option value="UNMATCHED">未匹配</Option>
                <Option value="AMBIGUOUS">待人工确认</Option>
                <Option value="FAILED">处理失败</Option>
                <Option value="SUMMARY">国家汇总</Option>
              </Select>
              <Button
                type="primary"
                icon="ios-search"
                :loading="appleFinancialLoading"
                @click="getAppleFinancialData()"
              >查询</Button>
              <Button icon="ios-refresh" @click="resetAppleFinancialSearch">重置</Button>
            </div>
            <div class="financial-import-actions">
              <Upload
                action="/"
                accept=".txt"
                :before-upload="handleAppleFinancialFile"
                :show-upload-list="false"
                :disabled="appleFinancialLoading"
              >
                <Button icon="ios-folder-open-outline">选择财务 TXT</Button>
              </Upload>
              <Button
                type="primary"
                icon="ios-cloud-upload-outline"
                :loading="appleFinancialImportLoading"
                :disabled="!appleFinancialFile"
                @click="handleAppleFinancialImport"
              >导入并匹配</Button>
              <Button
                icon="ios-refresh"
                :loading="appleFinancialRetryLoading"
                :disabled="!appleFinancialResult || !appleFinancialResult.batchId"
                @click="retryAppleFinancialPending"
              >重试待处理</Button>
            </div>
          </div>

          <div v-if="appleFinancialFile" class="selected-file apple-financial-file">
            <div class="file-info">
              <Icon type="ios-document-outline" size="22" />
              <span class="file-name">{{ appleFinancialFile.name }}</span>
              <span class="file-size">{{ formatFileSize(appleFinancialFile.size) }}</span>
            </div>
            <Button type="text" icon="ios-close" @click="appleFinancialFile = null" />
          </div>

          <div v-if="appleFinancialResult" class="apple-financial-summary">
            <div v-for="item in appleFinancialMetrics" :key="item.key" class="result-item">
              <span class="result-label">
                {{ item.label }}
                <Tooltip :content="item.tip" placement="top" :max-width="320">
                  <Icon type="ios-help-circle-outline" class="metric-help" />
                </Tooltip>
              </span>
              <strong class="result-value">{{ item.value }}</strong>
            </div>
          </div>

          <div ref="appleFinancialTableWrap" class="table-wrap">
            <Table
              border
              stripe
              :height="appleFinancialTableHeight"
              :loading="appleFinancialLoading"
              :data="appleFinancialTableData"
              :columns="appleFinancialColumns"
            >
              <template #appleFinancialAmount="{ row }">
                {{ formatMoney(row.extendedPartnerShare, row.partnerShareCurrency) }}
              </template>
              <template #appleFinancialCustomerPrice="{ row }">
                {{ formatMoney(row.customerPrice, row.customerCurrency) }}
              </template>
              <template #appleFinancialStatus="{ row }">
                <Tag :color="appleFinancialStatusColor(row.processStatus)">
                  {{ appleFinancialStatusText(row.processStatus) }}
                </Tag>
              </template>
              <Pages
                slot="footer"
                :page="appleFinancialPage"
                :func="getAppleFinancialData"
              />
            </Table>
          </div>
        </div>
      </TabPane>

      <TabPane label="Google 账单" name="google">
        <div class="google-panel">
          <div class="section-title">Google Earnings 账单导入</div>
          <Upload
            type="drag"
            action="/"
            accept=".csv,.xlsx,.xls"
            :before-upload="handleGoogleFile"
            :show-upload-list="false"
            :disabled="googleLoading"
            class="report-upload"
          >
            <div class="upload-content">
              <Icon type="ios-cloud-upload-outline" size="42" />
              <div class="upload-title">选择 CSV 或 Excel 账单</div>
              <div class="upload-subtitle">CSV、XLSX、XLS</div>
            </div>
          </Upload>

          <div v-if="googleFile" class="selected-file">
            <div class="file-info">
              <Icon type="ios-document-outline" size="22" />
              <span class="file-name">{{ googleFile.name }}</span>
              <span class="file-size">{{ formatFileSize(googleFile.size) }}</span>
            </div>
            <Button
              type="text"
              icon="ios-close"
              :disabled="googleLoading"
              @click="clearGoogleFile"
            />
          </div>

          <div class="google-actions">
            <Button
              type="primary"
              icon="ios-calculator-outline"
              :loading="googleLoading"
              :disabled="!googleFile"
              @click="handleGoogleImport"
            >导入并重算实收</Button>
          </div>

          <div v-if="googleResult || googleLoading || googleProcessData.length" class="result-section">
            <div class="result-heading">
              <div class="section-title">处理结果</div>
              <Tag v-if="googleLoading" color="blue">实时处理中，已完成 {{ googleProcessData.length }} 笔</Tag>
              <span v-else-if="googleStreamMessage" class="stream-message">{{ googleStreamMessage }}</span>
            </div>
            <div class="result-grid">
              <div v-for="item in googleMetrics" :key="item.key" class="result-item">
                <span class="result-label">
                  {{ item.label }}
                  <Tooltip :content="item.tip" placement="top" :max-width="300">
                    <Icon type="ios-help-circle-outline" class="metric-help" />
                  </Tooltip>
                </span>
                <strong class="result-value">{{ item.value }}</strong>
              </div>
            </div>
            <div v-if="googleProcessData.length" class="process-filter-bar">
              <Input
                v-model.trim="googleProcessFilters.keyword"
                clearable
                search
                placeholder="模糊查询 GPA、订单 ID 或处理说明"
                class="process-filter-keyword"
                @on-change="resetGoogleProcessPage"
              />
              <Select
                v-model="googleProcessFilters.processType"
                clearable
                placeholder="处理类型"
                class="process-filter-type"
                @on-change="resetGoogleProcessPage"
              >
                <Option value="PAYMENT">支付</Option>
                <Option value="REFUND">退款</Option>
                <Option value="PLATFORM_ADJUSTMENT">平台调整</Option>
              </Select>
              <DatePicker
                :value="googleProcessDateRange"
                type="daterange"
                format="yyyy-MM-dd"
                split-panels
                clearable
                placeholder="UTC+0 交易日期"
                class="process-filter-date"
                @on-change="handleGoogleProcessDateChange"
              />
              <Select
                v-model="googleProcessFilters.sortOrder"
                class="process-filter-sort"
                @on-change="resetGoogleProcessPage"
              >
                <Option value="DEFAULT">按处理顺序</Option>
                <Option value="UTC_DESC">UTC 时间倒序</Option>
                <Option value="UTC_ASC">UTC 时间正序</Option>
              </Select>
              <Button icon="md-refresh" @click="resetGoogleProcessFilters">重置</Button>
              <span class="process-filter-count">筛选后 {{ googleFilteredProcessData.length }} 笔</span>
            </div>
            <Table
              v-if="googleProcessData.length"
              border
              stripe
              size="small"
              :height="380"
              :columns="googleProcessColumns"
              :data="googleVisibleProcessData"
              class="process-table"
            >
              <template #processType="{ row }">
                <Tag :color="googleProcessTypeColor(row.processType)">
                  {{ googleProcessTypeText(row.processType) }}
                </Tag>
              </template>
              <template #platformReference="{ row }">
                <div v-if="row.processType === 'PLATFORM_ADJUSTMENT'">
                  <div>{{ row.adjustmentCode || '-' }}</div>
                  <small class="muted-text">{{ row.countryCode || '-' }} · {{ row.amountDirection || '-' }}</small>
                </div>
                <span v-else>{{ row.gpaOrderId || '-' }}</span>
              </template>
              <template #transactionTimes="{ row }">
                <div class="transaction-time-cell">
                  <div>{{ formatGoogleOriginalTime(row) }}</div>
                  <small class="muted-text">
                    UTC+0：{{ row.transactionUtcTime || row.transactionTime || '-' }}
                  </small>
                  <small v-if="row.transactionTimezone" class="muted-text">
                    {{ row.transactionTimezone }}（{{ formatUtcOffsetMinutes(row.transactionUtcOffsetMinutes) }}）
                  </small>
                </div>
              </template>
              <template #buyerAmount="{ row }">
                {{ formatMoney(row.buyerAmount, row.buyerCurrency) }}
              </template>
              <template #merchantNetAmount="{ row }">
                {{ formatMoney(row.merchantNetAmount, row.merchantCurrency) }}
              </template>
              <template #convertedNetAmount="{ row }">
                {{ formatMoney(row.convertedNetAmount, 'USD') }}
              </template>
              <template #processStatus="{ row }">
                <Tag :color="processStatusColor(row.processStatus)">
                  {{ processStatusText(row.processStatus) }}
                </Tag>
              </template>
            </Table>
            <div v-if="googleProcessData.length" class="process-pagination">
              <Page
                :current="googleProcessPage.current"
                :page-size="googleProcessPage.size"
                :total="googleFilteredProcessData.length"
                show-total
                show-elevator
                @on-change="page => { googleProcessPage.current = page }"
              />
            </div>
            <Alert
              v-if="googleResult.failureReasons && googleResult.failureReasons.length"
              type="warning"
              show-icon
              class="failure-alert"
            >
              <span>存在 {{ googleResult.failureReasons.length }} 条未处理记录</span>
            </Alert>
            <Table
              v-if="googleFailureData.length"
              border
              size="small"
              :columns="failureColumns"
              :data="googleFailureData"
              class="failure-table"
            />
          </div>
        </div>
      </TabPane>

      <TabPane label="Frankfurter 汇率" name="frankfurter">
        <div class="tab-panel">
          <div class="toolbar">
            <div class="filter-row">
              <Input
                v-model.trim="frankfurterSearchForm.baseCurrency"
                :maxlength="10"
                clearable
                placeholder="基准货币，如 HKD"
                style="width: 170px"
                @on-enter="handleFrankfurterSearch"
              />
              <Input
                v-model.trim="frankfurterSearchForm.quoteCurrency"
                :maxlength="10"
                clearable
                placeholder="目标货币，如 USD"
                style="width: 170px"
                @on-enter="handleFrankfurterSearch"
              />
              <DatePicker
                :value="frankfurterDateRange"
                :editable="false"
                clearable
                format="yyyy-MM-dd"
                type="daterange"
                placeholder="汇率日期"
                style="width: 230px"
                @on-change="handleFrankfurterDateChange"
              />
              <Button
                type="primary"
                icon="ios-search"
                :loading="frankfurterLoading"
                @click="handleFrankfurterSearch"
              >搜索</Button>
              <Button icon="ios-refresh" @click="handleFrankfurterReset">重置</Button>
            </div>
          </div>

          <div ref="frankfurterTableWrap" class="table-wrap">
            <Table
              border
              stripe
              :height="frankfurterTableHeight"
              :loading="frankfurterLoading"
              :data="frankfurterTableData"
              :columns="frankfurterColumns"
            >
              <template #rateRelation="{ row }">
                <span>1 {{ row.baseCurrency }} = {{ row.rate }} {{ row.quoteCurrency }}</span>
              </template>
              <Pages
                slot="footer"
                :page="frankfurterPage"
                :func="getFrankfurterData"
              />
            </Table>
          </div>
        </div>
      </TabPane>
    </Tabs>

    <Modal v-model="editVisible" title="编辑 Apple 汇率" :width="560">
      <Form
        v-if="editVisible"
        ref="editFormRef"
        :model="editForm"
        :rules="editRules"
        :label-width="120"
        @submit.native.prevent
      >
        <FormItem label="汇率日期">
          <Input :value="editForm.rateDate" disabled />
        </FormItem>
        <FormItem label="货币">
          <Input :value="editForm.currency" disabled />
        </FormItem>
        <FormItem label="兑美元汇率" prop="usdRate">
          <Input v-model.trim="editForm.usdRate" :maxlength="30" placeholder="请输入大于 0 的汇率" />
        </FormItem>
      </Form>
      <template #footer>
        <Button @click="editVisible = false">取消</Button>
        <Button type="primary" :loading="submitLoading" @click="handleSubmit">保存</Button>
      </template>
    </Modal>

    <Modal v-model="appleImportVisible" title="导入 Apple 汇率" :width="620">
      <Form
        ref="appleImportFormRef"
        :model="appleImportForm"
        :rules="appleImportRules"
        :label-width="110"
        @submit.native.prevent
      >
        <FormItem label="支付平台">
          <Input value="Apple 内购" disabled />
        </FormItem>
        <FormItem label="生效日期" prop="dateRange">
          <DatePicker
            :value="appleImportForm.dateRange"
            :editable="false"
            format="yyyy-MM-dd"
            type="daterange"
            placeholder="请选择汇率生效日期范围"
            style="width: 100%"
            @on-change="handleAppleDateChange"
          />
        </FormItem>
        <FormItem label="汇率文件">
          <Upload
            action="/"
            accept=".xlsx,.xls"
            :before-upload="handleAppleFile"
            :show-upload-list="false"
            :disabled="appleImportLoading"
          >
            <Button icon="ios-folder-open-outline">选择 Excel 文件</Button>
          </Upload>
          <div v-if="appleImportForm.file" class="inline-file">
            <Icon type="ios-document-outline" />
            <span>{{ appleImportForm.file.name }}</span>
            <Button
              type="text"
              icon="ios-close"
              :disabled="appleImportLoading"
              @click="appleImportForm.file = null"
            />
          </div>
        </FormItem>
      </Form>

      <Alert
        v-if="appleImportResult"
        :type="appleImportResult.failCount ? 'warning' : 'success'"
        show-icon
      >
        成功 {{ appleImportResult.successCount || 0 }} 条，失败 {{ appleImportResult.failCount || 0 }} 条
        <ul v-if="appleImportResult.failMessages && appleImportResult.failMessages.length" class="failure-list">
          <li v-for="(message, index) in appleImportResult.failMessages" :key="index">{{ message }}</li>
        </ul>
      </Alert>

      <template #footer>
        <Button @click="appleImportVisible = false">关闭</Button>
        <Button
          type="primary"
          icon="ios-cloud-upload-outline"
          :loading="appleImportLoading"
          @click="handleAppleImport"
        >开始导入</Button>
      </template>
    </Modal>
  </Card>
</template>

<script>
import Pages from '@/components/pages'
import {
  editExchangeRate,
  getFrankfurterExchangeRatePage,
  getAppleFinancialReportPage,
  getExchangeRatePage,
  importAppleFinancialReport,
  retryAppleFinancialReport,
  importExchangeRate,
  recalculateGoogleEarningsStream
} from '@/api/fee'

const APPLE_PLATFORM = 2
const MAX_FILE_SIZE = 20 * 1024 * 1024
const TABLE_FOOTER_HEIGHT = 54

// Apple财务账单的交易日期按UTC+0展示，避免浏览器本地时区造成日期前后偏移。
const formatUtcDate = value => {
  if (value === null || value === undefined || value === '') return '-'
  const normalizedValue = typeof value === 'string' && /^\d+$/.test(value)
    ? Number(value)
    : value
  const date = new Date(normalizedValue)
  if (Number.isNaN(date.getTime())) return String(value)
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export default {
  components: {
    Pages
  },
  data() {
    const validateRate = (rule, value, callback) => {
      if (value === null || value === undefined || value === '') {
        callback(new Error('请输入兑美元汇率'))
        return
      }
      const number = Number(value)
      if (!Number.isFinite(number) || number <= 0) {
        callback(new Error('汇率必须大于 0'))
        return
      }
      callback()
    }
    const validateDateRange = (rule, value, callback) => {
      if (!Array.isArray(value) || !value[0] || !value[1]) {
        callback(new Error('请选择汇率生效日期范围'))
        return
      }
      callback()
    }
    return {
      activeTab: 'apple',
      tableHeight: 320,
      tableLoading: false,
      statusLoadingId: null,
      submitLoading: false,
      editVisible: false,
      editForm: {
        id: null,
        rateDate: '',
        currency: '',
        usdRate: ''
      },
      editRules: {
        usdRate: [{ validator: validateRate, trigger: 'blur' }]
      },
      searchForm: {
        keyword: '',
        status: null,
        startDate: '',
        endDate: ''
      },
      searchDateRange: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      tableData: [],
      columns: [
        { title: '平台', key: 'platformName', width: 110, tooltip: true },
        { title: '汇率日期', key: 'rateDate', width: 120 },
        { title: '国家/地区', key: 'countryRegion', minWidth: 130, tooltip: true },
        { title: '货币', key: 'currencyCode', width: 100 },
        { title: '兑美元汇率', key: 'usdRate', minWidth: 140 },
        { title: '更新时间', key: 'updateTime', width: 170 },
        { title: '状态', slot: 'status', width: 90, align: 'center' },
        { title: '操作', slot: 'action', width: 90, align: 'center', fixed: 'right' }
      ],
      appleImportVisible: false,
      appleImportLoading: false,
      appleImportForm: {
        platform: APPLE_PLATFORM,
        dateRange: [],
        file: null
      },
      appleImportRules: {
        dateRange: [{ validator: validateDateRange, trigger: 'change' }]
      },
      appleImportResult: null,
      appleFinancialLoaded: false,
      appleFinancialLoading: false,
      appleFinancialImportLoading: false,
      appleFinancialRetryLoading: false,
      appleFinancialFile: null,
      appleFinancialResult: null,
      appleFinancialTableHeight: 320,
      appleFinancialSearch: {
        keyword: '',
        processStatus: ''
      },
      appleFinancialPage: {
        current: 1,
        size: 10,
        total: 0
      },
      appleFinancialTableData: [],
      appleFinancialColumns: [
        { title: '行号', key: 'reportRowNumber', width: 80, align: 'center', resizable: true },
        {
          title: '交易日期（UTC+0）',
          key: 'transactionDate',
          width: 170,
          resizable: true,
          render: (h, { row }) => h('span', formatUtcDate(row.transactionDate))
        },
        { title: 'SKU', key: 'sku', width: 180, tooltip: true, resizable: true },
        { title: '商品名称', key: 'title', width: 220, tooltip: true, resizable: true },
        { title: '国家', key: 'countryOfSale', width: 90, align: 'center', resizable: true },
        { title: '数量', key: 'quantity', width: 90, align: 'right', resizable: true },
        { title: '开发者分成', slot: 'appleFinancialAmount', width: 160, align: 'right', resizable: true },
        { title: '顾客价格', slot: 'appleFinancialCustomerPrice', width: 150, align: 'right', resizable: true },
        { title: '方向', key: 'saleOrReturn', width: 80, align: 'center', resizable: true },
        { title: '候选数', key: 'candidateCount', width: 90, align: 'right', resizable: true },
        { title: '关联订单 ID', key: 'matchedOrderIds', width: 190, tooltip: true, resizable: true },
        { title: '通知明细 ID', key: 'notificationDetailIds', width: 170, tooltip: true, resizable: true },
        { title: '实收记录', key: 'settlementRecordIds', width: 180, tooltip: true, resizable: true },
        { title: '匹配状态', slot: 'appleFinancialStatus', width: 130, align: 'center', resizable: true },
        { title: '处理说明', key: 'processMessage', width: 360, tooltip: true, resizable: true }
      ],
      googleFile: null,
      googleLoading: false,
      googleResult: null,
      googleProcessData: [],
      googleStreamMessage: '',
      googleProcessPage: {
        current: 1,
        size: 20
      },
      googleProcessFilters: {
        keyword: '',
        processType: '',
        startDate: '',
        endDate: '',
        sortOrder: 'DEFAULT'
      },
      googleProcessDateRange: [],
      frankfurterLoaded: false,
      frankfurterLoading: false,
      frankfurterTableHeight: 320,
      frankfurterSearchForm: {
        baseCurrency: '',
        quoteCurrency: '',
        startDate: '',
        endDate: ''
      },
      frankfurterDateRange: [],
      frankfurterPage: {
        current: 1,
        size: 10,
        total: 0
      },
      frankfurterTableData: [],
      frankfurterColumns: [
        { title: '汇率日期', key: 'rateDate', width: 140 },
        { title: '基准货币', key: 'baseCurrency', width: 130 },
        { title: '目标货币', key: 'quoteCurrency', width: 130 },
        { title: '汇率', key: 'rate', minWidth: 180 },
        { title: '换算关系', slot: 'rateRelation', minWidth: 260, tooltip: true },
        { title: '更新时间', key: 'updateTime', width: 180 }
      ],
      failureColumns: [
        { title: '序号', key: 'index', width: 80, align: 'center' },
        { title: '未处理原因', key: 'reason', minWidth: 420, tooltip: true }
      ],
      googleProcessColumns: [
        { title: '序号', key: 'index', width: 70, align: 'center', fixed: 'left', resizable: true },
        { title: '类型', slot: 'processType', width: 90, align: 'center', fixed: 'left', resizable: true },
        { title: 'GPA / 调整项目', slot: 'platformReference', width: 280, resizable: true },
        { title: '订单 ID', key: 'orderId', width: 120, resizable: true },
        { title: '交易时间', slot: 'transactionTimes', width: 285, resizable: true },
        { title: '买家金额', slot: 'buyerAmount', width: 150, align: 'right', resizable: true },
        { title: '商户结算净额', slot: 'merchantNetAmount', width: 170, align: 'right', resizable: true },
        { title: 'USD 实收/冲减/调整', slot: 'convertedNetAmount', width: 190, align: 'right', resizable: true },
        { title: '汇率来源', key: 'exchangeRateSource', width: 210, tooltip: true, resizable: true },
        { title: '处理状态', slot: 'processStatus', width: 130, align: 'center', resizable: true },
        { title: '处理说明', key: 'message', width: 320, tooltip: true, resizable: true }
      ]
    }
  },
  computed: {
    appleFinancialMetrics() {
      const result = this.appleFinancialResult || {}
      return [
        { key: 'detail', label: '交易明细', value: result.detailRowCount || 0, tip: 'Apple文件上半部分可参与订单匹配的交易明细行数。' },
        { key: 'summary', label: '国家汇总', value: result.summaryRowCount || 0, tip: '文件下半部分Country Of Sale汇总行，仅归档用于财务核对。' },
        { key: 'matched', label: '已确认订单', value: result.matchedOrderCount || 0, tip: '严格匹配成功并已写入实际实收的支付或退款订单数。' },
        { key: 'unmatched', label: '未匹配', value: result.unmatchedRowCount || 0, tip: '没有找到同时满足日期、SKU、国家、顾客价格/币种及方向的Apple通知。' },
        { key: 'ambiguous', label: '待人工确认', value: result.ambiguousRowCount || 0, tip: '候选数量与账单Quantity不一致，或同组出现多个Partner Share，系统不会自动分摊。' },
        { key: 'failed', label: '处理失败', value: result.failureCount || 0, tip: '文件格式或落库异常导致未完成处理的明细行。' }
      ]
    },
    googleMetrics() {
      const result = this.googleResult || {}
      return [
        { key: 'total', label: '账单数据行', value: result.totalRowCount || 0, tip: '本次Google账单文件中读取到的原始数据行总数。' },
        { key: 'payment', label: '支付订单重算', value: result.paymentCount || 0, tip: '已关联本地订单并新增或更新支付实收的支付记录数。' },
        { key: 'refund', label: '退款记录重算', value: result.refundCount || 0, tip: '已关联本地订单并新增或更新退款实收的退款记录数。' },
        { key: 'adjustment', label: '平台结算调整', value: result.platformAdjustmentCount || 0, tip: '不归属于单笔订单的Google平台级费用，例如巴西、台湾预提税。' },
        { key: 'reconcile', label: '待人工关联', value: result.reconcileExceptionCount || 0, tip: '平台账单存在记录，但无法唯一关联本地订单，已进入对账异常池。' },
        { key: 'pending', label: '待换算 USD', value: result.usdPendingCount || 0, tip: '商户结算币种不是USD且暂时缺少可用汇率，尚未得到USD参考值。' },
        { key: 'ignored', label: '忽略报表行', value: result.ignoredRowCount || 0, tip: '不是支付、退款或已支持的平台调整类型，本次未参与实收计算的报表行。' }
      ]
    },
    googleFailureData() {
      const reasons = this.googleResult && this.googleResult.failureReasons
      return (reasons || []).map((reason, index) => ({
        index: index + 1,
        reason
      }))
    },
    googleFilteredProcessData() {
      const filters = this.googleProcessFilters
      const keyword = (filters.keyword || '').toLowerCase()
      const data = this.googleProcessData.filter(item => {
        const keywordMatched = !keyword || [
          item.gpaOrderId,
          item.orderId,
          item.adjustmentCode,
          item.message
        ].some(value => String(value == null ? '' : value).toLowerCase().includes(keyword))
        const typeMatched = !filters.processType || item.processType === filters.processType
        const utcDate = (item.transactionUtcTime || item.transactionTime || '').slice(0, 10)
        const startMatched = !filters.startDate || (utcDate && utcDate >= filters.startDate)
        const endMatched = !filters.endDate || (utcDate && utcDate <= filters.endDate)
        return keywordMatched && typeMatched && startMatched && endMatched
      })
      if (filters.sortOrder === 'DEFAULT') return data
      return data.sort((left, right) => {
        const leftTime = left.transactionUtcTime || left.transactionTime || ''
        const rightTime = right.transactionUtcTime || right.transactionTime || ''
        if (!leftTime && !rightTime) return left.index - right.index
        if (!leftTime) return 1
        if (!rightTime) return -1
        const comparison = leftTime.localeCompare(rightTime)
        return filters.sortOrder === 'UTC_ASC' ? comparison : -comparison
      })
    },
    googleVisibleProcessData() {
      const start = (this.googleProcessPage.current - 1) * this.googleProcessPage.size
      return this.googleFilteredProcessData.slice(start, start + this.googleProcessPage.size)
    }
  },
  methods: {
    getData(page) {
      this.page.current = page || 1
      const params = {
        current: this.page.current,
        size: this.page.size,
        platform: APPLE_PLATFORM,
        keyword: this.searchForm.keyword || undefined,
        status: this.searchForm.status,
        startDate: this.searchForm.startDate || undefined,
        endDate: this.searchForm.endDate || undefined
      }
      this.tableLoading = true
      return getExchangeRatePage(params)
        .then(({ data }) => {
          this.tableData = data.records || []
          this.page.current = data.current
          this.page.total = data.total
        })
        .finally(() => {
          this.tableLoading = false
          this.$nextTick(this.updateTableHeight)
        })
    },
    handleTabChange(name) {
      if (name === 'apple') {
        this.$nextTick(this.updateTableHeight)
      } else if (name === 'appleFinancial') {
        if (!this.appleFinancialLoaded) {
          this.getAppleFinancialData()
        } else {
          this.$nextTick(this.updateAppleFinancialTableHeight)
        }
      } else if (name === 'frankfurter') {
        if (!this.frankfurterLoaded) {
          this.getFrankfurterData()
        } else {
          this.$nextTick(this.updateFrankfurterTableHeight)
        }
      }
    },
    updateTableHeight() {
      const tableWrap = this.$refs.appleTableWrap
      if (!tableWrap) return
      const availableHeight = Math.floor(tableWrap.getBoundingClientRect().height)
      if (availableHeight > 0) {
        // Table 的 height 不包含 footer 插槽，需要给分页单独预留高度。
        this.tableHeight = Math.max(160, availableHeight - TABLE_FOOTER_HEIGHT)
      }
    },
    updateFrankfurterTableHeight() {
      const tableWrap = this.$refs.frankfurterTableWrap
      if (!tableWrap) return
      const availableHeight = Math.floor(tableWrap.getBoundingClientRect().height)
      if (availableHeight > 0) {
        this.frankfurterTableHeight = Math.max(160, availableHeight - TABLE_FOOTER_HEIGHT)
      }
    },
    updateAppleFinancialTableHeight() {
      const tableWrap = this.$refs.appleFinancialTableWrap
      if (!tableWrap) return
      const availableHeight = Math.floor(tableWrap.getBoundingClientRect().height)
      if (availableHeight > 0) {
        this.appleFinancialTableHeight = Math.max(160, availableHeight - TABLE_FOOTER_HEIGHT)
      }
    },
    getAppleFinancialData(page) {
      this.appleFinancialPage.current = page || 1
      const params = {
        current: this.appleFinancialPage.current,
        size: this.appleFinancialPage.size,
        batchId: this.appleFinancialResult && this.appleFinancialResult.batchId,
        keyword: this.appleFinancialSearch.keyword || undefined,
        processStatus: this.appleFinancialSearch.processStatus || undefined
      }
      this.appleFinancialLoading = true
      return getAppleFinancialReportPage(params)
        .then(({ data }) => {
          this.appleFinancialTableData = data.records || []
          this.appleFinancialPage.current = data.current
          this.appleFinancialPage.total = data.total
          this.appleFinancialLoaded = true
        })
        .finally(() => {
          this.appleFinancialLoading = false
          this.$nextTick(this.updateAppleFinancialTableHeight)
        })
    },
    resetAppleFinancialSearch() {
      this.appleFinancialSearch = { keyword: '', processStatus: '' }
      this.getAppleFinancialData()
    },
    handleAppleFinancialFile(file) {
      if (!this.validateFile(file, ['txt'])) return false
      this.appleFinancialFile = file
      return false
    },
    handleAppleFinancialImport() {
      if (!this.appleFinancialFile) {
        this.$Message.warning('请选择Apple财务账单TXT文件')
        return
      }
      const formData = new FormData()
      formData.append('file', this.appleFinancialFile)
      this.appleFinancialImportLoading = true
      importAppleFinancialReport(formData)
        .then(({ data }) => {
          this.appleFinancialResult = data
          this.appleFinancialSearch = { keyword: '', processStatus: '' }
          this.$Message.success(data.message || 'Apple财务账单处理完成')
          return this.getAppleFinancialData()
        })
        .finally(() => {
          this.appleFinancialImportLoading = false
        })
    },
    retryAppleFinancialPending() {
      const batchId = this.appleFinancialResult && this.appleFinancialResult.batchId
      if (!batchId) return
      this.appleFinancialRetryLoading = true
      retryAppleFinancialReport(batchId)
        .then(({ data }) => {
          this.appleFinancialResult = data
          this.$Message.success(data.message || 'Apple账单待处理明细重试完成')
          return this.getAppleFinancialData()
        })
        .finally(() => {
          this.appleFinancialRetryLoading = false
        })
    },
    appleFinancialStatusText(status) {
      const statusMap = {
        MATCHED: '已匹配',
        UNMATCHED: '未匹配',
        AMBIGUOUS: '待人工确认',
        SUMMARY: '国家汇总',
        FAILED: '处理失败'
      }
      return statusMap[status] || status || '-'
    },
    appleFinancialStatusColor(status) {
      const colorMap = {
        MATCHED: 'green',
        UNMATCHED: 'orange',
        AMBIGUOUS: 'gold',
        SUMMARY: 'blue',
        FAILED: 'red'
      }
      return colorMap[status] || 'default'
    },
    getFrankfurterData(page) {
      this.frankfurterPage.current = page || 1
      const params = {
        current: this.frankfurterPage.current,
        size: this.frankfurterPage.size,
        baseCurrency: this.normalizeCurrency(this.frankfurterSearchForm.baseCurrency),
        quoteCurrency: this.normalizeCurrency(this.frankfurterSearchForm.quoteCurrency),
        startDate: this.frankfurterSearchForm.startDate || undefined,
        endDate: this.frankfurterSearchForm.endDate || undefined
      }
      this.frankfurterLoading = true
      return getFrankfurterExchangeRatePage(params)
        .then(({ data }) => {
          this.frankfurterTableData = data.records || []
          this.frankfurterPage.current = data.current
          this.frankfurterPage.total = data.total
          this.frankfurterLoaded = true
        })
        .finally(() => {
          this.frankfurterLoading = false
          this.$nextTick(this.updateFrankfurterTableHeight)
        })
    },
    handleFrankfurterSearch() {
      this.getFrankfurterData()
    },
    handleFrankfurterDateChange(value) {
      this.frankfurterDateRange = value || []
      this.frankfurterSearchForm.startDate = value && value[0] ? value[0] : ''
      this.frankfurterSearchForm.endDate = value && value[1] ? value[1] : ''
    },
    handleFrankfurterReset() {
      this.frankfurterSearchForm = {
        baseCurrency: '',
        quoteCurrency: '',
        startDate: '',
        endDate: ''
      }
      this.frankfurterDateRange = []
      this.getFrankfurterData()
    },
    normalizeCurrency(value) {
      const currency = (value || '').trim().toUpperCase()
      return currency || undefined
    },
    handleSearch() {
      this.getData()
    },
    handleSearchDateChange(value) {
      this.searchDateRange = value || []
      this.searchForm.startDate = value && value[0] ? value[0] : ''
      this.searchForm.endDate = value && value[1] ? value[1] : ''
    },
    handleReset() {
      this.searchForm = {
        keyword: '',
        status: null,
        startDate: '',
        endDate: ''
      }
      this.searchDateRange = []
      this.getData()
    },
    handleOpenEditModal(row) {
      this.editForm = {
        id: row.id,
        rateDate: row.rateDate || '',
        currency: row.currency || row.currencyCode || '',
        usdRate: row.usdRate === null || row.usdRate === undefined ? '' : String(row.usdRate)
      }
      this.editVisible = true
      this.$nextTick(() => {
        if (this.$refs.editFormRef) this.$refs.editFormRef.resetFields()
        this.editForm.usdRate = row.usdRate === null || row.usdRate === undefined ? '' : String(row.usdRate)
      })
    },
    handleSubmit() {
      this.$refs.editFormRef.validate(valid => {
        if (!valid) return
        this.submitLoading = true
        editExchangeRate({
          id: this.editForm.id,
          usdRate: this.editForm.usdRate
        })
          .then(() => {
            this.$Message.success('汇率已更新')
            this.editVisible = false
            this.getData(this.page.current)
          })
          .finally(() => {
            this.submitLoading = false
          })
      })
    },
    handleChangeStatus(row, enabled) {
      const status = enabled ? 0 : 1
      this.statusLoadingId = row.id
      editExchangeRate({ id: row.id, status })
        .then(() => {
          row.status = status
          this.$Message.success(enabled ? '已启用' : '已禁用')
        })
        .finally(() => {
          this.statusLoadingId = null
        })
    },
    openAppleImportModal() {
      this.appleImportForm = {
        platform: APPLE_PLATFORM,
        dateRange: [],
        file: null
      }
      this.appleImportResult = null
      this.appleImportVisible = true
      this.$nextTick(() => {
        if (this.$refs.appleImportFormRef) this.$refs.appleImportFormRef.resetFields()
      })
    },
    handleAppleDateChange(value) {
      this.appleImportForm.dateRange = value || []
      if (this.$refs.appleImportFormRef) {
        this.$refs.appleImportFormRef.validateField('dateRange')
      }
    },
    handleAppleFile(file) {
      if (!this.validateFile(file, ['xls', 'xlsx'])) return false
      this.appleImportForm.file = file
      this.appleImportResult = null
      return false
    },
    handleAppleImport() {
      this.$refs.appleImportFormRef.validate(valid => {
        if (!valid) return
        if (!this.appleImportForm.file) {
          this.$Message.warning('请选择 Apple 汇率文件')
          return
        }
        const formData = new FormData()
        formData.append('file', this.appleImportForm.file)
        formData.append('platform', APPLE_PLATFORM)
        formData.append('startDate', this.appleImportForm.dateRange[0])
        formData.append('endDate', this.appleImportForm.dateRange[1])
        this.appleImportLoading = true
        importExchangeRate(formData)
          .then(({ data }) => {
            this.appleImportResult = data
            this.$Message.success('Apple 汇率文件处理完成')
            this.getData()
          })
          .finally(() => {
            this.appleImportLoading = false
          })
      })
    },
    handleGoogleFile(file) {
      if (!this.validateFile(file, ['csv', 'xls', 'xlsx'])) return false
      this.googleFile = file
      this.googleResult = null
      this.googleProcessData = []
      this.googleStreamMessage = ''
      this.resetGoogleProcessFilters()
      return false
    },
    clearGoogleFile() {
      this.googleFile = null
      this.googleResult = null
      this.googleProcessData = []
      this.googleStreamMessage = ''
      this.resetGoogleProcessFilters()
    },
    handleGoogleImport() {
      if (!this.googleFile) {
        this.$Message.warning('请选择 Google Earnings 账单')
        return
      }
      const formData = new FormData()
      formData.append('file', this.googleFile)
      this.googleResult = {
        totalRowCount: 0,
        paymentCount: 0,
        refundCount: 0,
        platformAdjustmentCount: 0,
        reconcileExceptionCount: 0,
        usdPendingCount: 0,
        ignoredRowCount: 0,
        failureReasons: []
      }
      this.googleProcessData = []
      this.googleStreamMessage = '正在读取账单文件'
      this.resetGoogleProcessFilters()
      this.googleLoading = true
      recalculateGoogleEarningsStream(formData, this.handleGoogleStreamEvent)
        .then(summary => {
          if (summary) this.googleResult = summary
          this.googleStreamMessage = summary && summary.idempotentReuse
            ? '相同账单文件已经处理完成，本次复用历史结果'
            : `处理完成，共处理 ${this.googleProcessData.length} 笔`
          this.$Message.success(this.googleStreamMessage)
        })
        .catch(error => {
          this.googleStreamMessage = error.message || 'Google 账单处理失败'
          this.$Message.error(this.googleStreamMessage)
        })
        .finally(() => {
          this.googleLoading = false
        })
    },
    handleGoogleStreamEvent(event) {
      if (!event) return
      if (event.eventType === 'START') {
        this.googleStreamMessage = event.message || 'Google 账单开始处理'
        return
      }
      if (event.eventType === 'ITEM' && event.item) {
        const item = {
          ...event.item,
          index: this.googleProcessData.length + 1
        }
        this.googleProcessData.push(item)
        this.googleProcessPage.current = Math.max(
          1,
          Math.ceil(this.googleFilteredProcessData.length / this.googleProcessPage.size)
        )
        if (item.processStatus === 'RECONCILE_PENDING') {
          this.googleResult.reconcileExceptionCount += 1
        } else if (item.processStatus !== 'FAILED') {
          if (item.processType === 'REFUND') {
            this.googleResult.refundCount += 1
          } else if (item.processType === 'PLATFORM_ADJUSTMENT') {
            this.googleResult.platformAdjustmentCount += 1
          } else {
            this.googleResult.paymentCount += 1
          }
          if (item.processStatus === 'USD_PENDING') this.googleResult.usdPendingCount += 1
        }
        return
      }
      if (event.eventType === 'SUMMARY' && event.summary) {
        this.googleResult = event.summary
        return
      }
      if (event.eventType === 'ERROR') {
        this.googleStreamMessage = event.message || 'Google 账单处理失败'
        // 每个GPA独立提交，任务级错误只补充失败原因，已显示的成功结果仍然有效。
        const failureReasons = this.googleResult.failureReasons || []
        this.googleResult.failureReasons = [...failureReasons, this.googleStreamMessage]
      }
    },
    googleProcessTypeText(type) {
      const typeMap = {
        PAYMENT: '支付',
        REFUND: '退款',
        PLATFORM_ADJUSTMENT: '平台调整'
      }
      return typeMap[type] || type || '-'
    },
    googleProcessTypeColor(type) {
      const colorMap = {
        PAYMENT: 'blue',
        REFUND: 'orange',
        PLATFORM_ADJUSTMENT: 'purple'
      }
      return colorMap[type] || 'default'
    },
    processStatusText(status) {
      const statusMap = {
        SUCCESS: '成功',
        USD_PENDING: 'USD 待确认',
        RECONCILE_PENDING: '待人工关联',
        FAILED: '失败'
      }
      return statusMap[status] || status || '-'
    },
    processStatusColor(status) {
      const colorMap = {
        SUCCESS: 'green',
        USD_PENDING: 'gold',
        RECONCILE_PENDING: 'blue',
        FAILED: 'red'
      }
      return colorMap[status] || 'default'
    },
    resetGoogleProcessPage() {
      this.googleProcessPage.current = 1
    },
    handleGoogleProcessDateChange(value) {
      this.googleProcessDateRange = value || []
      this.googleProcessFilters.startDate = value && value[0] ? value[0] : ''
      this.googleProcessFilters.endDate = value && value[1] ? value[1] : ''
      this.resetGoogleProcessPage()
    },
    resetGoogleProcessFilters() {
      this.googleProcessFilters.keyword = ''
      this.googleProcessFilters.processType = ''
      this.googleProcessFilters.startDate = ''
      this.googleProcessFilters.endDate = ''
      this.googleProcessFilters.sortOrder = 'DEFAULT'
      this.googleProcessDateRange = []
      this.resetGoogleProcessPage()
    },
    formatGoogleOriginalTime(row) {
      const original = [row.transactionDateText, row.transactionTimeText]
        .filter(Boolean)
        .join(' ')
      return original || row.transactionLocalTime || '-'
    },
    formatUtcOffsetMinutes(offsetMinutes) {
      if (offsetMinutes === null || offsetMinutes === undefined || offsetMinutes === '') {
        return 'UTC偏移未知'
      }
      const value = Number(offsetMinutes)
      if (Number.isNaN(value)) return 'UTC偏移未知'
      const absoluteMinutes = Math.abs(value)
      const hours = Math.floor(absoluteMinutes / 60)
      const minutes = absoluteMinutes % 60
      const minuteText = minutes ? `:${minutes < 10 ? `0${minutes}` : minutes}` : ''
      return `UTC${value >= 0 ? '+' : '-'}${hours}${minuteText}`
    },
    formatMoney(amount, currency) {
      if (amount === null || amount === undefined || amount === '') return '-'
      return `${amount} ${currency || ''}`.trim()
    },
    validateFile(file, extensions) {
      const extension = (file.name.split('.').pop() || '').toLowerCase()
      if (!extensions.includes(extension)) {
        this.$Message.warning('文件格式不正确')
        return false
      }
      if (file.size > MAX_FILE_SIZE) {
        this.$Message.warning('文件大小不能超过 20 MB')
        return false
      }
      return true
    },
    formatFileSize(size) {
      if (!size) return '0 KB'
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
      return `${(size / 1024 / 1024).toFixed(1)} MB`
    }
  },
  mounted() {
    this.getData()
    window.addEventListener('resize', this.updateTableHeight)
    window.addEventListener('resize', this.updateFrankfurterTableHeight)
    window.addEventListener('resize', this.updateAppleFinancialTableHeight)
    this.$nextTick(this.updateTableHeight)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateTableHeight)
    window.removeEventListener('resize', this.updateFrankfurterTableHeight)
    window.removeEventListener('resize', this.updateAppleFinancialTableHeight)
  }
}
</script>

<style scoped lang="less">
.layout-card {
  height: 100%;

  /deep/ .ivu-card-body {
    height: 100%;
    padding: 16px 18px;
  }
}

.rate-tabs {
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;

  /deep/ .ivu-tabs-bar {
    flex: 0 0 auto;
  }

  /deep/ .ivu-tabs-content {
    height: auto;
    min-height: 0;
    flex: 1;
  }

  /deep/ .ivu-tabs-tabpane {
    height: 100%;
  }
}

.tab-panel {
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.filter-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.financial-import-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.apple-financial-file {
  flex: 0 0 auto;
  margin-top: 0;
  margin-bottom: 12px;
}

.apple-financial-summary {
  display: flex;
  flex: 0 0 auto;
  overflow-x: auto;
  margin-bottom: 12px;
  border: 1px solid #dcdee2;
  border-radius: 4px;
}

.table-wrap {
  min-height: 0;
  flex: 1;
  overflow: hidden;
}

.google-panel {
  width: 100%;
  height: 100%;
  min-height: 0;
  padding: 4px 2px 28px;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
}

.section-title {
  margin-bottom: 14px;
  color: #17233d;
  font-size: 16px;
  font-weight: 600;
}

.report-upload {
  /deep/ .ivu-upload-drag {
    border-radius: 4px;
    background: #fafbfc;
  }
}

.upload-content {
  padding: 34px 20px;
  color: #515a6e;
}

.upload-content .ivu-icon {
  color: #2d8cf0;
}

.upload-title {
  margin-top: 8px;
  color: #17233d;
  font-size: 15px;
}

.upload-subtitle {
  margin-top: 4px;
  color: #808695;
  font-size: 12px;
}

.selected-file,
.inline-file {
  display: flex;
  min-height: 42px;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  padding: 6px 10px;
  border: 1px solid #dcdee2;
  border-radius: 4px;
  background: #fff;
}

.file-info,
.inline-file {
  gap: 8px;
}

.file-name {
  max-width: 580px;
  overflow: hidden;
  color: #17233d;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-size {
  color: #808695;
  font-size: 12px;
}

.google-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 16px;
}

.result-section {
  margin-top: 30px;
  padding-top: 22px;
  border-top: 1px solid #e8eaec;
}

.result-heading {
  display: flex;
  min-height: 28px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.stream-message {
  color: #808695;
  font-size: 13px;
}

.muted-text {
  color: #808695;
  font-size: 12px;
}

.result-grid {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  border: 1px solid #dcdee2;
  border-radius: 4px;
}

.result-item {
  display: flex;
  min-width: 150px;
  min-height: 58px;
  flex: 1 0 150px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 8px 12px;
  border-right: 1px solid #dcdee2;
}

.result-item:last-child {
  border-right: 0;
}

.result-label {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: #808695;
  font-size: 13px;
  white-space: nowrap;
}

.result-value {
  color: #17233d;
  font-size: 20px;
  line-height: 1;
}

.metric-help {
  color: #2d8cf0;
  cursor: help;
  font-size: 15px;
}

.process-filter-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  flex-wrap: wrap;
}

.process-filter-keyword {
  width: 280px;
}

.process-filter-type {
  width: 140px;
}

.process-filter-date {
  width: 240px;
}

.process-filter-sort {
  width: 150px;
}

.process-filter-count {
  color: #808695;
  font-size: 12px;
}

.transaction-time-cell {
  display: flex;
  flex-direction: column;
  line-height: 1.55;
}

.failure-alert,
.failure-table,
.process-table {
  margin-top: 14px;
}

.process-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

.failure-list {
  max-height: 150px;
  margin: 8px 0 0 18px;
  overflow: auto;
}

@media (max-width: 900px) {
  .toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .process-filter-keyword,
  .process-filter-type,
  .process-filter-date,
  .process-filter-sort {
    width: 100%;
  }
}
</style>
